import { customUseEffect } from "@common";
import {
  getActivityApi,
  getActivityNameById,
  getActivityStatus,
  getActivityType,
  getCrmPriority,
  getProjectUser,
} from "@features";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useFormik } from "formik";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

dayjs.extend(customParseFormat);

const validationSchema = Yup.object({
  activityType: Yup.mixed().required("Activity Type is required"),
  activityName: Yup.mixed().required("Activity Name is required"),
  dueDate: Yup.mixed().required("Due Date is required"),
  actualDate: Yup.mixed().nullable(),
  priority: Yup.mixed().nullable().required("Priority is required"),
  description: Yup.string()
    .max(500, "Description should contain below 500 characters")
    .nullable(),
  assignedTo: Yup.mixed().nullable(),
  status: Yup.mixed().nullable(),
  outComeNote: Yup.string()
    .max(500, "Outcome Note should contain below 500 characters")
    .nullable(),
});

export const getFieldErrorProps = (formik, fieldName) => {
  const isTouched = Boolean(formik?.touched?.[fieldName]);
  const errorMsg = formik?.errors?.[fieldName];

  if (!isTouched || !errorMsg) {
    return { error: false, helperText: "" };
  }

  return {
    error: true,
    helperText: String(errorMsg),
  };
};

const sanitizeId = (id) => {
  if (id === true || id === false || id === "true" || id === "false" || id === undefined || id === null) {
    return null;
  }
  return id;
};

const getSelectedId = (data, fallbackId) => {
  const cleanFallback = sanitizeId(fallbackId);
  if (Array.isArray(data)) {
    return sanitizeId(data[0]?.id || data[0]?._id) || cleanFallback || null;
  }
  return sanitizeId(data?.id || data?._id) || cleanFallback || null;
};

const isRequestSuccessful = (res) =>
  Boolean(
    res?.payload?.ok ||
    res?.payload?.data?.ok ||
    res?.meta?.requestStatus === "fulfilled" ||
    !res?.error
  );

const resolveFindOption = (value, options, key) => {
  if (!value) return null;
  if (typeof value === "object") return value;
  return (
    options.find(
      (opt) =>
        opt?.[key] === value ||
        opt?.activityStatus === value ||
        opt?.status === value ||
        opt?.name === value ||
        opt?.id === value
    ) || (key ? { [key]: value } : value)
  );
};

const resolveFindUser = (assignedTo, projectUsersOptions) => {
  if (!assignedTo) return null;
  if (typeof assignedTo === "object") return assignedTo;
  return (
    projectUsersOptions.find(
      (u) =>
        u?.id === assignedTo ||
        u?.userId === assignedTo ||
        u?.email === assignedTo ||
        u?.userName === assignedTo
    ) || { userName: assignedTo, id: assignedTo }
  );
};

export default function useActivityForm({
  forCampaign,
  forProspect,
  forInquiry,
  forLead,
  forOpportunity,
  forAccount,
  forContact,
  forContract,
  forOrder,
  forCustomer,
  campaignId,
  prospectId,
  inquiryId,
  leadId,
  opportunityId,
  customerAccountId,
  contactId,
  contractId,
  orderId,
  customerId,
  data,
  onSuccess,
  ...props
}) {
  const dispatch = useDispatch();

  const campaignSelectedData = useSelector((state) => state?.campaign?.selectedData);
  const prospectSelectedData = useSelector((state) => state?.prospect?.selectedData);
  const inquirySelectedData = useSelector((state) => state?.inquiry?.selectedData);
  const leadSelectedData = useSelector((state) => state?.lead?.selectedData);
  const opportunitySelectedData = useSelector((state) => state?.opportunity?.selectedData);
  const accountSelectedData = useSelector((state) => state?.account?.selectedData);
  const contactSelectedData = useSelector((state) => state?.contact?.selectedData);
  const contractSelectedData = useSelector((state) => state?.contract?.selectedData);
  const orderSelectedData = useSelector((state) => state?.order?.selectedData);
  const customerSelectedData = useSelector((state) => state?.customer?.selectedData);

  const propsWithFlags = {
    forCampaign,
    forProspect,
    forInquiry,
    forLead,
    forOpportunity,
    forAccount,
    forContact,
    forContract,
    forOrder,
    forCustomer,
    campaignId,
    prospectId,
    inquiryId,
    leadId,
    opportunityId,
    customerAccountId,
    contactId,
    contractId,
    orderId,
    customerId,
    ...props,
  };

  const api = useMemo(
    () => getActivityApi(propsWithFlags),
    [
      forCampaign,
      forProspect,
      forInquiry,
      forLead,
      forOpportunity,
      forAccount,
      forContact,
      forContract,
      forOrder,
      forCustomer,
      campaignId,
      prospectId,
      inquiryId,
      leadId,
      opportunityId,
      customerAccountId,
      contactId,
      contractId,
      orderId,
      customerId,
      props?.customerId,
      props?.entityType,
    ]
  );

  const activeEntityId = useMemo(() => {
    const propId = sanitizeId(propsWithFlags?.[api.idKey]);
    if (propId && (typeof propId === "string" || typeof propId === "number")) {
      return propId;
    }
    let resolved = null;
    switch (api.entityType) {
      case "contract":
        resolved = sanitizeId(contractId) || getSelectedId(contractSelectedData, data?.contractId);
        break;
      case "customer-account":
        resolved = sanitizeId(customerAccountId) || getSelectedId(accountSelectedData, data?.customerAccountId);
        break;
      case "contact":
        resolved = sanitizeId(contactId) || getSelectedId(contactSelectedData, data?.contactId);
        break;
      case "opportunity":
        resolved = sanitizeId(opportunityId) || getSelectedId(opportunitySelectedData, data?.opportunityId);
        break;
      case "lead":
        resolved = sanitizeId(leadId) || getSelectedId(leadSelectedData, data?.leadId);
        break;
      case "campaign":
        resolved = sanitizeId(campaignId) || getSelectedId(campaignSelectedData, data?.campaignId);
        break;
      case "inquiry":
        resolved = sanitizeId(inquiryId) || getSelectedId(inquirySelectedData, data?.inquiryId);
        break;
      case "prospect":
        resolved = sanitizeId(prospectId) || getSelectedId(prospectSelectedData, data?.prospectId);
        break;
      case "customer":
        resolved = sanitizeId(customerId) || sanitizeId(props?.customerId) || getSelectedId(customerSelectedData, data?.customerId);
        break;
      case "order":
        resolved = sanitizeId(orderId) || getSelectedId(orderSelectedData, data?.orderId);
        break;
      default:
        resolved = null;
    }
    return sanitizeId(resolved);
  }, [
    api.entityType,
    api.idKey,
    propsWithFlags,
    contractId,
    customerAccountId,
    contactId,
    opportunityId,
    leadId,
    campaignId,
    inquiryId,
    prospectId,
    orderId,
    customerId,
    props?.customerId,
    contractSelectedData,
    accountSelectedData,
    contactSelectedData,
    opportunitySelectedData,
    leadSelectedData,
    campaignSelectedData,
    inquirySelectedData,
    prospectSelectedData,
    customerSelectedData,
    orderSelectedData,
    data,
  ]);

  const activityTypeOptions = useSelector((state) => state?.activityType?.data) || [];
  const activityNameOptions = useSelector((state) => state?.activityName?.data) || [];
  const projectUsersOptions = useSelector((state) => state?.projectUsers?.data) || [];
  const activityStatusOptions = useSelector((state) => state?.activityStatus?.data) || [];
  const crmPriorityOptions = useSelector((state) => state?.crmPriority?.data) || [];

  customUseEffect(() => {
    dispatch(getActivityType());
    dispatch(getProjectUser());
    dispatch(getActivityStatus());
    dispatch(getCrmPriority());
  }, [dispatch]);

  const findActivityType = useMemo(
    () => resolveFindOption(data?.activityType, activityTypeOptions, "activityType"),
    [data?.activityType, activityTypeOptions]
  );

  const findActivityName = useMemo(
    () => resolveFindOption(data?.activityName, activityNameOptions, "activityName"),
    [data?.id, data?.activityName]
  );

  const findUser = useMemo(
    () => resolveFindUser(data?.assignedTo || data?.assignedToId, projectUsersOptions),
    [data?.assignedTo, data?.assignedToId, projectUsersOptions]
  );

  const findStatus = useMemo(
    () => resolveFindOption(data?.status, activityStatusOptions, "activityStatus"),
    [data?.status, activityStatusOptions]
  );

  const findPriority = useMemo(
    () => resolveFindOption(data?.priority, crmPriorityOptions, "priorityType"),
    [data?.priority, crmPriorityOptions]
  );

  const formatDateTimeValue = (val) => {
    if (!val) return null;
    if (dayjs.isDayjs(val)) {
      return val.isValid() ? val.toISOString() : null;
    }
    const parsed = dayjs(val);
    return parsed.isValid() ? parsed.toISOString() : null;
  };

  const initialValues = useMemo(
    () => ({
      activityType: findActivityType,
      activityName: findActivityName,
      dueDate: data?.dueDate || null,
      actualDate: data?.actualDate || null,
      priority: findPriority,
      description: data?.description || "",
      assignedTo: findUser,
      status: findStatus,
      outComeNote: data?.outComeNote || "",
    }),
    [
      data?.id,
      data?.updatedOn,
      findActivityType,
      findActivityName,
      findPriority,
      findUser,
      findStatus,
      data?.dueDate,
      data?.actualDate,
      data?.description,
      data?.outComeNote,
    ]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      const cleanEntityId = sanitizeId(activeEntityId);
      const payload = {
        ...(data?.id ? { id: data.id } : {}),
        ...(cleanEntityId ? { [api.idKey]: cleanEntityId } : {}),
        activityType:
          typeof values?.activityType === "object"
            ? values?.activityType?.activityType || values?.activityType?.name || ""
            : values?.activityType || "",
        activityName:
          typeof values?.activityName === "object"
            ? values?.activityName?.activityName || values?.activityName?.name || ""
            : values?.activityName || "",
        dueDate: formatDateTimeValue(values?.dueDate),
        actualDate: formatDateTimeValue(values?.actualDate),
        priority:
          typeof values?.priority === "object"
            ? values?.priority?.priorityType || values?.priority?.name || ""
            : values?.priority || null,
        description: values?.description || "",
        assignedToId:
          values?.assignedTo?.id ||
          values?.assignedTo?.userId ||
          (typeof values?.assignedTo === "string" ? values?.assignedTo : null),
        status:
          typeof values?.status === "object"
            ? values?.status?.activityStatus || values?.status?.status || values?.status?.name || ""
            : values?.status || "",
        outComeNote: values?.outComeNote || "",
      };

      const action = data?.id ? api.update(payload) : api.add(payload);
      const res = await dispatch(action);

      if (isRequestSuccessful(res)) {
        onSuccess?.();
        return true;
      }
      return false;
    },
  });

  const selectedActivityType = formik?.values?.activityType;

  customUseEffect(() => {
    if (selectedActivityType === null) return;
    const rawType = selectedActivityType || data?.activityType || data?.activityTypeId;
    const matchedType =
      typeof rawType === "object"
        ? rawType
        : activityTypeOptions.find(
          (opt) =>
            opt?.activityType === rawType ||
            opt?.name === rawType ||
            opt?.id === rawType
        );

    const typeId =
      matchedType?.id ||
      matchedType?._id ||
      matchedType?.activityTypeId ||
      (typeof rawType === "string" || typeof rawType === "number" ? rawType : null);

    if (typeId) {
      dispatch(getActivityNameById({ activityTypeId: typeId }));
    }
  }, [selectedActivityType, data?.activityType, data?.activityTypeId, activityTypeOptions, dispatch]);

  return {
    formik,
    options: {
      activityTypeOptions,
      activityNameOptions,
      projectUsersOptions,
      activityStatusOptions,
      crmStatusOptions: activityStatusOptions,
      crmPriorityOptions,
    },
  };
}
